<template>
  <div class="pb-3 pt-2 text-white" data-onboarding-video-snipping="tool-bar">
    <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      <div class="justify-self-start">
        <u-tooltip
          :text="startClippingTooltip"
          data-onboarding-video-snipping="create-clip"
        >
          <button
            class="h-10 w-40 px-4 rounded-xl border border-zinc-600 bg-zinc-800 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            @click="startNewClip"
            :disabled="isCursorInClaimedZone"
          >
            <u-icon name="i-ri-scissors-cut-line" class="w-5 h-5" />
            <span>Create Clip</span>
          </button>
        </u-tooltip>
      </div>

      <div class="justify-self-center flex items-center gap-4">
        <u-tooltip text="Rewind 50 Milliseconds">
          <button class="text-white transition-colors hover:text-gray-300" @click="skip(-0.05)" data-onboarding-video-snipping="rewind">
            <u-icon name="i-ri-rewind-mini-fill" class="w-6 h-6" />
          </button>
        </u-tooltip>

        <u-tooltip :text="isPlaying ? 'Pause' : 'Play'">
          <button class="text-white transition-colors hover:text-gray-300" @click="togglePlay">
            <u-icon :name="isPlaying ? 'i-ri-pause-fill' : 'i-ri-play-fill'" class="w-8 h-8" />
          </button>
        </u-tooltip>

        <u-tooltip text="Forward 50 Milliseconds">
          <button class="text-white transition-colors hover:text-gray-300" @click="skip(0.05)" data-onboarding-video-snipping="forward">
            <u-icon name="i-ri-speed-mini-fill" class="w-6 h-6" />
          </button>
        </u-tooltip>
      </div>

      <div class="justify-self-end flex items-center gap-3">
        <input
          class="w-28 accent-white"
          type="range"
          min="0"
          max="1"
          step="0.01"
          v-model.number="volume"
          @input="setVolume"
          aria-label="Volume"
        />
        <span class="min-w-[160px] text-right text-white text-md font-medium tabular-nums">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const props = defineProps({
    player: Object, // Video.js player instance
    currentTimeSeconds: {
      type: Number,
      default: 0
    }
  });

  const isPlaying = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const volume = ref(1);

  const videoEditorStore = useVideoEditorStore();
  const {clips, selectedMarker} = storeToRefs(videoEditorStore);
  const CLIP_BOUNDARY_SNAP_EPSILON = 0.01;
  const CLIP_MARKER_ROOM_EPSILON = 0.001;

  function getClipRange(clip) {
    const markerTimes = (clip?.markers || [])
      .map(marker => Number(marker?.time))
      .filter(time => Number.isFinite(time));

    if (markerTimes.length < 2) {
      return null;
    }

    return {
      start: Math.min(...markerTimes),
      end: Math.max(...markerTimes)
    };
  }

  function normalizeToNearestClipBoundary(time) {
    const safeTime = Number(time);
    if (!Number.isFinite(safeTime)) {
      return safeTime;
    }

    let nearestBoundary = safeTime;
    let nearestDistance = Infinity;

    for (const clip of clips.value) {
      const range = getClipRange(clip);
      if (!range) {
        continue;
      }

      for (const boundary of [range.start, range.end]) {
        const distance = Math.abs(safeTime - boundary);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestBoundary = boundary;
        }
      }
    }

    return nearestDistance <= CLIP_BOUNDARY_SNAP_EPSILON ? nearestBoundary : safeTime;
  }

  function isTimeInsideExistingClip(time) {
    return clips.value.some(clip => {
      const range = getClipRange(clip);
      if (!range) {
        return false;
      }

      return time > range.start && time < range.end;
    });
  }

  function getNextClipStartBoundary(time) {
    const maxTimelineTime = Number(duration.value);
    let rightBoundary = Number.isFinite(maxTimelineTime) && maxTimelineTime > 0 ? maxTimelineTime : Infinity;

    for (const clip of clips.value) {
      const range = getClipRange(clip);
      if (!range) {
        continue;
      }

      if (range.start >= time) {
        rightBoundary = Math.min(rightBoundary, range.start);
      }
    }

    return rightBoundary;
  }

  const clipStartBlockedReason = computed(() => {
    const maxTimelineTime = Number(duration.value);
    if (!Number.isFinite(maxTimelineTime) || maxTimelineTime <= 0) {
      return 'Video is still loading';
    }

    const markerTime = normalizeToNearestClipBoundary(currentTime.value);

    if (isTimeInsideExistingClip(markerTime)) {
      return 'Position is inside an existing clip';
    }

    if ((getNextClipStartBoundary(markerTime) - markerTime) < VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS - CLIP_MARKER_ROOM_EPSILON) {
      return `Not enough room for a ${VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS} second clip here.`;
    }

    return '';
  });

  const startClippingTooltip = computed(() => {
    return clipStartBlockedReason.value || 'Create Clip';
  });

  const isCursorInClaimedZone = computed(() => {
    return Boolean(clipStartBlockedReason.value);
  });

  const formatTime = inject('formatTime');

  function togglePlay() {
    if (!props.player) return;
    if (props.player.paused()) {
      props.player.play();
    } else {
      props.player.pause();
    }
  }

  function skip(seconds) {
    if (!props.player) return;
    const clampedTime = Math.max(0, Math.min(props.player.currentTime() + seconds, duration.value));

    if (selectedMarker.value) {
      const didAdjust = videoEditorStore.adjustMarker({
        marker: selectedMarker.value,
        time: clampedTime,
        maxTime: duration.value || Infinity
      });

      if (!didAdjust) {
        return;
      }
    }

    props.player.currentTime(clampedTime);
    updateState();
  }

  function setVolume() {
    if (!props.player) {
      return;
    }

    props.player.volume(volume.value);
  }

  function updateState() {
    if (!props.player) return;
    currentTime.value = props.player.currentTime();
    duration.value = props.player.duration();
    isPlaying.value = !props.player.paused();
    volume.value = props.player.volume();
  }

  watch(() => props.currentTimeSeconds, (nextTime) => {
    if (!Number.isFinite(nextTime)) {
      return;
    }

    currentTime.value = nextTime;
  }, {
    immediate: true
  });

  function startNewClip() {
    const markerTime = normalizeToNearestClipBoundary(props.player.currentTime());
    const maxTimelineTime = Number(duration.value);

    if (
      !Number.isFinite(maxTimelineTime)
      || maxTimelineTime <= 0
      || isTimeInsideExistingClip(markerTime)
      || (getNextClipStartBoundary(markerTime) - markerTime) < VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS - CLIP_MARKER_ROOM_EPSILON
    ) {
      return;
    }

    useGtm().trackEvent({
      event: 'core_ui-video_editor-start_clipping_btn',
      action: 'click',
      category: 'video_editor'
    });

    videoEditorStore.createClipAtTime({
      startTime: markerTime,
      maxTime: maxTimelineTime
    });
  }

  onMounted(() => {
    if (!props.player) return;

    props.player.on('timeupdate', updateState);
    props.player.on('play', updateState);
    props.player.on('pause', updateState);
    props.player.on('volumechange', updateState);
    props.player.on('loadedmetadata', updateState);

    updateState();
  });

  onUnmounted(() => {
    if (!props.player) return;

    props.player.off('timeupdate', updateState);
    props.player.off('play', updateState);
    props.player.off('pause', updateState);
    props.player.off('volumechange', updateState);
    props.player.off('loadedmetadata', updateState);
  });

</script>
